import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { useCart, imgUrl, inr } from "../../store/CartContext";

export function QtyControl({ value, onChange, max = 50, testid }) {
  return (
    <div className="inline-flex items-center rounded-full border border-[#3915AA]/20">
      <button onClick={() => onChange(value - 1)} disabled={value <= 1} data-testid={`${testid}-dec`} aria-label="Decrease" className="p-1.5 text-[#3915AA] disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
      <span data-testid={`${testid}-value`} className="w-7 text-center text-sm font-bold text-[#1E0A5C]">{value}</span>
      <button onClick={() => onChange(value + 1)} disabled={value >= max} data-testid={`${testid}-inc`} aria-label="Increase" className="p-1.5 text-[#3915AA] disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export function FreeShippingBar() {
  const { subtotal, settings } = useCart();
  const left = settings.free_shipping_above - subtotal;
  const pct = Math.min(100, (subtotal / settings.free_shipping_above) * 100);
  return (
    <div data-testid="free-shipping-bar" className="rounded-xl bg-[#F7F5FF] p-3">
      <p className="flex items-center gap-2 text-xs font-semibold text-[#1E0A5C]">
        <Truck className="h-4 w-4 text-[#FC5106]" />
        {left > 0 ? <>Add <span className="text-[#FC5106]">{inr(left)}</span> more for FREE delivery</> : "Yay! You get FREE delivery"}
      </p>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#3915AA]/10"><div className="h-full rounded-full bg-[#FC5106] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, shipping, total, mrpTotal } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col border-0 bg-white p-0 text-[#1E0A5C] sm:max-w-md [&>button]:text-[#3915AA]">
        <div className="border-b border-[#3915AA]/10 px-5 py-4">
          <SheetTitle className="font-heading text-xl text-[#1E0A5C]">Your cart</SheetTitle>
          <SheetDescription className="text-xs text-[#5E5675]">{items.length ? `${items.length} item${items.length > 1 ? "s" : ""}` : "Nothing here yet"}</SheetDescription>
        </div>
        {!items.length ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag className="h-12 w-12 text-[#3915AA]/30" />
            <p className="text-sm text-[#5E5675]">Your cart is empty. Treat your pet to something nice!</p>
            <Link to="/shop" onClick={() => setOpen(false)} data-testid="cart-drawer-shop" className="rounded-full bg-[#FC5106] px-6 py-2.5 text-sm font-bold text-white">Start shopping</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <FreeShippingBar />
              {items.map((i) => (
                <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-3">
                  <img src={imgUrl(i.image)} alt="" className="h-20 w-20 flex-shrink-0 rounded-xl bg-[#F7F5FF] object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-semibold">{i.name}</p>
                    <p className="text-xs text-[#5E5675]">{i.variant_label}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <QtyControl value={i.qty} max={i.max} onChange={(q) => setQty(i.key, q)} testid={`cart-item-qty-${i.key}`} />
                      <p className="text-sm font-bold">{inr(i.price * i.qty)}</p>
                    </div>
                  </div>
                  <button onClick={() => remove(i.key)} data-testid={`cart-item-remove-${i.key}`} aria-label="Remove" className="self-start p-1 text-[#5E5675] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-[#3915AA]/10 px-5 py-4 text-sm">
              <div className="flex justify-between"><span className="text-[#5E5675]">Subtotal</span><span className="font-semibold">{inr(subtotal)}</span></div>
              {mrpTotal > subtotal && <div className="flex justify-between text-green-600"><span>You save</span><span className="font-semibold">{inr(mrpTotal - subtotal)}</span></div>}
              <div className="flex justify-between"><span className="text-[#5E5675]">Delivery</span><span className="font-semibold">{shipping ? inr(shipping) : "FREE"}</span></div>
              <div className="flex justify-between pt-1 text-base font-bold"><span>Total</span><span data-testid="cart-drawer-total">{inr(total)}</span></div>
              <button onClick={() => { setOpen(false); navigate("/checkout"); }} data-testid="cart-drawer-checkout-button" className="mt-2 w-full rounded-full bg-[#FC5106] py-3.5 text-sm font-bold text-white hover:bg-[#E24804]">
                Checkout · {inr(total)}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
